<template>
  <div class="salon-app salon-rate">
    <header class="salon-topbar">
      <div class="salon-topbar-row">
        <button class="salon-close" type="button" :aria-label="$t('salon.rate.close')" @click="$emit('back')">
          <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" stroke-width="2"
              stroke-linecap="round" />
          </svg>
        </button>
        <h1 class="salon-topbar-title">{{ $t("salon.rate.title") }}</h1>
      </div>
    </header>

    <main class="salon-main">
      <section class="salon-intro">
        <h2 class="salon-intro-title">{{ $t("salon.rate.heading") }}</h2>
        <p class="salon-intro-text">
          {{ $t("salon.rate.intro") }}
        </p>
        <!-- Who is actually being rated. The headline asks about "your visit", but the vote
             lands on one stylist, and the customer should see whose name it goes against. -->
        <p v-if="stylistName" class="salon-intro-who">
          {{ stylistName }}<span v-if="serviceSummary"> · {{ serviceSummary }}</span>
        </p>
      </section>

      <section class="salon-vote-block">
        <div class="salon-stars" role="radiogroup" :aria-label="$t('salon.rate.ratingLabel')">
          <button
            v-for="star in 5"
            :key="star"
            class="salon-star"
            :class="{ 'salon-star--on': star <= rating }"
            type="button"
            role="radio"
            :aria-checked="star === rating"
            :aria-label="star === 1 ? $t('salon.vote.oneStar') : $t('salon.vote.stars', { count: star })"
            @click="rating = star"
          >
            <svg viewBox="0 0 24 24" width="36" height="36" aria-hidden="true">
              <path d="M12 3l2.6 5.6 6.1.8-4.5 4.2 1.2 6-5.4-3-5.4 3 1.2-6L3.3 9.4l6.1-.8L12 3z"
                fill="currentColor" />
            </svg>
          </button>
        </div>

        <div class="salon-chips">
          <button
            v-for="chip in chips"
            :key="chip"
            class="salon-chip"
            :class="{ 'salon-chip--on': selectedChips.includes(chip) }"
            type="button"
            :aria-pressed="selectedChips.includes(chip)"
            @click="toggleChip(chip)"
          >
            {{ chipLabel(chip) }}
          </button>
        </div>
      </section>

      <section class="salon-note">
        <label class="salon-note-label" for="salon-rate-comment">
          {{ $t("salon.rate.detailsLabel") }}
        </label>
        <textarea
          id="salon-rate-comment"
          v-model="comment"
          class="salon-note-box"
          rows="4"
          maxlength="800"
          :placeholder="$t('salon.rate.placeholder')"
        ></textarea>
      </section>

      <p v-if="errorMessage" class="salon-error">{{ errorMessage }}</p>
    </main>

    <nav class="salon-cta">
      <button
        class="salon-cta-btn"
        type="button"
        :disabled="!rating || isSaving"
        @click="submit"
      >
        {{ isSaving ? $t("salon.rate.submitting") : $t("salon.rate.submit") }}
      </button>
    </nav>

    <material-loading :active="isSaving" />
  </div>
</template>

<script>
import MaterialLoading from "@/components/MaterialLoading.vue";
import SalonBookingService from "@/services/salonBooking.service";
import "@/salon/styles/tokens.css";

// SH046 — the chips stay English in state and in the comment the merchant reads; only the
// label on the button follows the customer's language.
const CHIP_KEYS = {
  "Great cut": "salon.rate.chips.greatCut",
  Friendly: "salon.rate.chips.friendly",
  "Clean salon": "salon.rate.chips.cleanSalon",
  "Long wait": "salon.rate.chips.longWait",
};

/**
 * SH017 — "Vote for stylist".
 *
 * Reached from a completed, paid appointment in My bookings. The appointment is passed in
 * whole rather than re-fetched: the history screen already holds it, and the only thing this
 * screen adds to it is the vote.
 *
 * The chips and the free-text box are one field by the time they leave here. The ratings table
 * stores a rating and a comment, and "Great cut, Friendly - the fade was perfect" is a comment;
 * a separate column for the chips would hold something nothing reads back.
 */
export default {
  name: "SalonRateStylist",
  components: {
    MaterialLoading,
  },
  props: {
    booking: {
      type: Object,
      required: true,
    },
  },
  emits: ["back", "rated"],
  data() {
    return {
      rating: 0,
      selectedChips: [],
      comment: "",
      isSaving: false,
      errorMessage: "",
      // Fixed prompts rather than free text alone: most people will not type, and four
      // taps still tell the salon what went right or wrong.
      chips: ["Great cut", "Friendly", "Clean salon", "Long wait"],
    };
  },
  computed: {
    stylistName() {
      return this.booking.staff_name || "";
    },

    serviceSummary() {
      return this.booking.service_summary || "";
    },
  },
  methods: {
    chipLabel(chip) {
      return CHIP_KEYS[chip] ? this.$t(CHIP_KEYS[chip]) : chip;
    },

    toggleChip(chip) {
      const at = this.selectedChips.indexOf(chip);

      if (at === -1) {
        // Pushed in the order tapped, not the order displayed: the comment reads back as
        // what the customer said, in the order they said it.
        this.selectedChips.push(chip);
        return;
      }

      this.selectedChips.splice(at, 1);
    },

    /**
     * The chips and the note as the one string the API stores.
     *
     * " - " rather than a newline: this lands in a comment column a merchant reads in a
     * table cell, where a line break is invisible.
     */
    buildComment() {
      const note = this.comment.trim();
      const chips = this.selectedChips.join(", ");

      if (chips && note) {
        return chips + " - " + note;
      }

      return chips || note;
    },

    async submit() {
      if (!this.rating || this.isSaving) {
        return;
      }

      this.isSaving = true;
      this.errorMessage = "";

      try {
        const response = await SalonBookingService.rateBooking({
          booking_code: this.booking.booking_code,
          rating: this.rating,
          comment: this.buildComment(),
        });

        if (response.data.result.isSuccess) {
          // The API hands back the booking re-presented — can_rate false, my_rating filled.
          // That is the row My bookings has to redraw, so it travels with the event.
          this.$emit("rated", response.data.data.booking);
          return;
        }

        // Every refusal the API can give here is a specific one worth showing: the bill was
        // not settled, somebody already voted, the appointment was cancelled.
        this.errorMessage =
          response.data.result.message || this.$t("salon.rate.saveError");
      } catch (error) {
        this.errorMessage = this.$t("salon.rate.networkError");
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style scoped>
/* Tokens come from src/salon/styles/tokens.css, namespaced under .salon-app. */
.salon-rate {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
  max-width: var(--salon-max-width);
  margin: 0 auto;
  background: var(--salon-background);
  color: var(--salon-on-background);
  font-family: var(--salon-font-body);
  font-size: 14px;
  line-height: 20px;
}

/* ── Top app bar ───────────────────────────────────────────────── */
.salon-topbar {
  position: sticky;
  top: 0;
  z-index: 40;
  display: flex;
  flex-direction: column;
  padding: 16px var(--salon-container-padding) 12px;
  background: var(--salon-surface);
}

.salon-topbar-row {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
}

/* A close, not a back arrow: this screen is a detour off the history list, and the reference
   marks that difference the same way. */
.salon-close {
  position: absolute;
  left: -8px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  color: var(--salon-primary);
  background: transparent;
  border: 0;
  cursor: pointer;
}

.salon-close:active {
  transform: scale(0.95);
}

/* material-dashboard forces Roboto Slab onto every h1-h3 globally, and that beats a scoped
   rule that simply omits font-family. Every heading in this file therefore names its own
   face — see the MTH027 PaymentSuccess reskin for the same trap. */
.salon-topbar-title {
  margin: 0;
  font-family: var(--salon-font-body);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.12em;
  line-height: 16px;
  text-transform: uppercase;
  color: var(--salon-primary);
}

/* ── Canvas ────────────────────────────────────────────────────── */
.salon-main {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--salon-stack-lg);
  padding: var(--salon-stack-lg) var(--salon-container-padding) 140px;
}

.salon-intro {
  display: flex;
  flex-direction: column;
  gap: var(--salon-stack-sm);
  text-align: center;
}

.salon-intro-title {
  margin: 0;
  font-family: var(--salon-font-headline);
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
  color: var(--salon-on-surface);
}

.salon-intro-text {
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  color: var(--salon-on-surface-variant);
}

.salon-intro-who {
  margin: 4px 0 0;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.02em;
  color: var(--salon-on-surface-variant);
}

/* ── Stars & chips ─────────────────────────────────────────────── */
.salon-vote-block {
  display: flex;
  flex-direction: column;
  gap: var(--salon-stack-lg);
  align-items: center;
}

.salon-stars {
  display: flex;
  gap: 12px;
}

.salon-star {
  display: flex;
  padding: 2px;
  /* Unlit stars are the same grey the past-booking rail uses, so an unrated visit reads as
     "not yet" rather than as a bad review. */
  color: var(--salon-surface-container-highest);
  background: transparent;
  border: 0;
  cursor: pointer;
  transition: color 0.2s ease, transform 0.15s ease;
}

.salon-star--on {
  color: var(--salon-gold);
}

.salon-star:active {
  transform: scale(0.9);
}

.salon-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
}

.salon-chip {
  padding: 9px 16px;
  font-family: var(--salon-font-body);
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: 0.02em;
  color: var(--salon-on-surface);
  background: var(--salon-surface-lowest);
  border: 1px solid var(--salon-surface-container-highest);
  border-radius: 999px;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
}

.salon-chip--on {
  color: var(--salon-on-primary);
  background: var(--salon-primary);
  border-color: var(--salon-primary);
}

/* ── Note ──────────────────────────────────────────────────────── */
.salon-note {
  display: flex;
  flex-direction: column;
  gap: var(--salon-stack-sm);
}

.salon-note-label {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.05em;
  line-height: 16px;
  color: var(--salon-on-surface);
}

.salon-note-box {
  width: 100%;
  padding: 14px;
  font-family: var(--salon-font-body);
  font-size: 14px;
  line-height: 20px;
  color: var(--salon-on-surface);
  resize: none;
  background: var(--salon-surface-lowest);
  border: 1px solid var(--salon-border);
  border-radius: var(--salon-radius-input);
  box-shadow: var(--salon-shadow-soft);
}

.salon-note-box:focus {
  border-color: var(--salon-primary);
  outline: none;
}

.salon-note-box::placeholder {
  color: var(--salon-on-surface-variant);
  opacity: 0.6;
}

.salon-error {
  margin: 0;
  font-size: 13px;
  line-height: 18px;
  color: var(--salon-error);
  text-align: center;
}

/* ── Bottom action ─────────────────────────────────────────────── */
.salon-cta {
  position: fixed;
  bottom: 0;
  left: 50%;
  z-index: 50;
  width: 100%;
  max-width: var(--salon-max-width);
  padding: 16px var(--salon-container-padding)
    calc(20px + env(safe-area-inset-bottom, 0px));
  background: rgba(251, 249, 246, 0.9);
  backdrop-filter: blur(12px);
  transform: translateX(-50%);
}

.salon-cta-btn {
  width: 100%;
  padding: 16px;
  font-family: var(--salon-font-body);
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  color: var(--salon-on-primary);
  background: var(--salon-primary);
  border: 0;
  border-radius: 999px;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.salon-cta-btn:disabled {
  cursor: default;
  opacity: 0.35;
}

.salon-cta-btn:not(:disabled):active {
  transform: scale(0.98);
}
</style>
